import { prefersReducedMotion } from '@theme/utilities';
import { throttle } from '@theme/utilities';

/**
 * A custom element that fades text from transparent to opaque based on scroll position.
 * Supports word-by-word animation tied to active scrolling.
 */
class ScrollFadeText extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #processed = false;

  /** @type {boolean} */
  #isInView = false;

  /** @type {HTMLElement[]} */
  #words = [];

  /** @type {(() => void) | null} */
  #handleScroll = null;

  /** @type {number} */
  #startingOpacity = 0.25;

  /** @type {number} */
  #opacityRange = 0.75;

  connectedCallback() {
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) {
      this.classList.add('scroll-fade-visible');
      return;
    }

    // Read starting opacity from CSS variable (defaults to 0.25 if not set)
    const computedStyle = getComputedStyle(this);
    const cssOpacity = parseFloat(computedStyle.getPropertyValue('--starting-opacity'));
    if (!isNaN(cssOpacity)) {
      this.#startingOpacity = cssOpacity;
      this.#opacityRange = 1 - cssOpacity;
    }

    // Get configuration from data attributes
    const wordByWord = this.dataset.wordByWord === 'true';

    // Wait for next frame to ensure text snippet has rendered
    requestAnimationFrame(() => {
      // Process text content into word spans if word-by-word mode
      if (wordByWord && !this.#processed) {
        this.#processTextIntoWords();
        this.#processed = true;
      }

      // Get all word elements
      this.#words = Array.from(this.querySelectorAll('.scroll-fade-word'));

      // Debug: Check if words were found
      if (this.#words.length === 0) {
        console.warn('ScrollFadeText: No character spans found. Text may not have been processed.');
        return;
      }

      // Setup scroll listener
      this.#handleScroll = throttle(() => this.#updateWordsBasedOnScroll(), 16);
      window.addEventListener('scroll', /** @type {EventListener} */ (this.#handleScroll), { passive: true });

      // Setup IntersectionObserver to know when element is in view
      this.#setupIntersectionObserver();

      // Initial check
      this.#updateWordsBasedOnScroll();
    });
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    if (this.#handleScroll) {
      window.removeEventListener('scroll', /** @type {EventListener} */ (this.#handleScroll));
    }
  }

  /**
   * Processes text content and wraps each word in a span
   */
  #processTextIntoWords() {
    // Find the text block content - try different selectors
    let contentElement = this.querySelector('.text-block > *');
    if (!contentElement) {
      contentElement = this.querySelector('.text-block');
    }
    if (!contentElement) {
      contentElement = this.querySelector('div, p, rte-formatter');
    }
    if (!contentElement) {
      console.warn('ScrollFadeText: Could not find content element');
      return;
    }

  /**
   * Recursively processes nodes and wraps characters in spans
   * @param {Node} node - The node to process
   * @returns {Node | DocumentFragment}
   */
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text.trim() === '') return node;

      // Split text into words first (to prevent word breaks)
      const words = text.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      // Use for loop for better performance
      for (let i = 0; i < words.length; i++) {
        const word = /** @type {string} */ (words[i]);
        
        if (word.trim() !== '') {
          // Create a wrapper for the word to prevent it from breaking across lines
          const wordWrapper = document.createElement('span');
          wordWrapper.className = 'scroll-fade-word-wrapper';
          
          // Split word into individual characters and create spans
          const chars = word.split('');
          for (let j = 0; j < chars.length; j++) {
            const span = document.createElement('span');
            span.className = 'scroll-fade-word';
            span.textContent = chars[j] || '';
            wordWrapper.appendChild(span);
          }
          
          fragment.appendChild(wordWrapper);
        } else if (word !== '') {
          // Preserve whitespace between words
          fragment.appendChild(document.createTextNode(word));
        }
      }

      return fragment;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Process child nodes recursively
      const newNode = node.cloneNode(false);
      const childNodes = node.childNodes;
      for (let i = 0; i < childNodes.length; i++) {
        const child = childNodes[i];
        if (child) {
          newNode.appendChild(processNode(child));
        }
      }
      return newNode;
    }

    return node.cloneNode(true);
  };

    // Process all children
    const newContent = document.createElement('div');
    newContent.className = contentElement.className;
    const childNodes = contentElement.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      const child = childNodes[i];
      if (child) {
        newContent.appendChild(processNode(child));
      }
    }

    contentElement.replaceWith(newContent);
  }

  /**
   * Updates word opacity based on scroll position
   */
  #updateWordsBasedOnScroll() {
    if (!this.#isInView || this.#words.length === 0) return;

    // Cache frequently accessed values
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const wordsLength = this.#words.length;
    
    // Speed up the animation by reducing the scroll range
    const scrollStart = viewportHeight * 0.8;
    const scrollEnd = viewportHeight * 0.2;
    const scrollRange = scrollStart - scrollEnd;
    const currentScroll = scrollStart - rect.top;
    const scrollProgress = Math.max(0, Math.min(1, currentScroll / scrollRange));

    // Pre-calculate common values outside the loop
    const wordsLengthInverse = 1 / wordsLength;
    
    // Ensure all characters reach 100% by the time scroll hits 85%
    // This gives the last characters enough room to complete
    const effectiveScrollProgress = Math.min(1, scrollProgress / 0.85);
    
    // Use for loop for better performance with large numbers of characters
    for (let i = 0; i < wordsLength; i++) {
      const wordProgress = i * wordsLengthInverse;
      
      // Calculate opacity for this character (starting opacity to 100%)
      let opacity;
      if (effectiveScrollProgress >= wordProgress) {
        // Calculate how far through this character's fade we are
        const progressBeyondThreshold = effectiveScrollProgress - wordProgress;
        // Each character fades over a proportion of the remaining scroll
        const fadeRange = wordsLengthInverse * 2; // Give each character 2x its proportional space
        const wordCompletion = Math.min(1, progressBeyondThreshold / fadeRange);
        opacity = this.#startingOpacity + (wordCompletion * this.#opacityRange);
      } else {
        opacity = this.#startingOpacity;
      }
      
      // Set opacity (convert to string for CSS)
      const word = this.#words[i];
      if (word) {
        word.style.opacity = String(opacity);
      }
    }
  }

  /**
   * Sets up the IntersectionObserver to track when element is in viewport
   */
  #setupIntersectionObserver() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#isInView = entry.isIntersecting;
          
          // Always update on intersection change (allows fade out when scrolling up)
          this.#updateWordsBasedOnScroll();
        });
      },
      {
        threshold: 0,
        rootMargin: '20% 0px 20% 0px',
      }
    );

    this.#intersectionObserver.observe(this);
  }
}

// Register the custom element
if (!customElements.get('scroll-fade-text')) {
  customElements.define('scroll-fade-text', ScrollFadeText);
}

